import React, { useState } from 'react';
import axios from 'axios';
import { Bell, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { API } from '../lib/shop';

export const NotifyMeForm = ({ compact = false }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setError('');
    try {
      const { data } = await axios.post(`${API}/shop/notify`, { email });
      setResult(data);
      setStatus('done');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : Array.isArray(d) ? 'Please enter a valid email address.' : 'Something went wrong. Please try again in a moment.');
      setStatus('error');
    }
  };

  if (status === 'done' && result) {
    return (
      <div className="rounded-2xl bg-white/80 backdrop-blur border border-pink-100 p-5 text-left" data-testid="notify-success">
        <p className="flex items-center gap-2 font-semibold text-gray-900">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          {result.already_subscribed ? "You're already on the list" : "You're on the list!"}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          We've saved <strong>{result.email}</strong> and will email you the moment the shop opens.
        </p>
        {result.confirmation_sent ? (
          <p className="text-xs text-green-700 mt-2" data-testid="notify-confirmation-sent">A confirmation email has been sent to you.</p>
        ) : (
          <p className="text-xs text-amber-700 mt-2" data-testid="notify-confirmation-pending">Your signup is saved. Confirmation emails are not enabled yet, so you won't receive one right now.</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="text-left" data-testid="notify-form">
      <div className={`flex ${compact ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row'} gap-3`}>
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter your email"
          data-testid="notify-email-input"
          className="flex-1 h-14 px-5 rounded-full border-2 border-pink-100 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-pink-400 focus:ring-4 focus:ring-pink-100 transition"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid="notify-submit-button"
          className="h-14 px-8 rounded-full bg-gray-900 text-white font-semibold hover:bg-pink-600 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
        >
          {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
          Notify Me
        </button>
      </div>
      {status === 'error' && (
        <p className="flex items-center gap-2 text-sm text-red-600 mt-3" data-testid="notify-error"><AlertTriangle className="w-4 h-4" /> {error}</p>
      )}
      <p className="text-xs text-gray-500 mt-3">No spam. One email when we launch, then you're in control.</p>
    </form>
  );
};
